/**
 * 050 T010 — the public report at `/r/$token`.
 *
 * No login. The token is the whole credential, exactly as `/q/$token` works, and a `SECURITY
 * DEFINER` function is the only thing that can turn one into data.
 *
 * The range is PINNED. A recipient opening a link from a four-week-old email sees that week, not
 * this one. A page that quietly tracked "the last 7 days" would show them current numbers while
 * they believe they are reading the period the email discussed — a misinformation bug, and worse
 * than a visible error because nobody reports it.
 *
 * An expired or unknown token gets an explanation. Never a blank page, never a bare 404: the
 * recipient is a customer or an accountant who did nothing wrong, and "this link has expired" is
 * information while an empty screen looks like the product is broken.
 */
import { createFileRoute } from '@tanstack/react-router'
import { Loader2, LinkIcon, Printer } from 'lucide-react'
import { usePublicReport, normaliseConfig } from '@/hooks/use-reports'
import { ReportHeader, ReportSections } from '@/components/report/sections'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/r/$token')({ component: PublicReport })

const FALLBACK_ACCENT = '#0038a8'

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-canvas px-4 py-8 print:bg-white print:p-0">
      <div className="mx-auto max-w-3xl rounded-[14px] border border-token bg-token-surface p-6 shadow-[0_1px_2px_var(--shadow-color)] print:max-w-none print:rounded-none print:border-0 print:p-0 print:shadow-none">
        {children}
      </div>
    </div>
  )
}

function PublicReport() {
  const { token } = Route.useParams()
  const { data, isLoading, isError } = usePublicReport(token)

  if (isLoading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 py-10 text-token-secondary">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading the report…
        </div>
      </Shell>
    )
  }

  const meta = data?.meta
  const problem = isError ? 'not_found' : meta?.error

  if (problem === 'expired') {
    const when = meta?.expired_at ? phFormat(new Date(meta.expired_at), 'date', { day: 'numeric', month: 'long', year: 'numeric' }) : null
    return (
      <Shell>
        <LinkIcon className="h-6 w-6 text-token-muted" aria-hidden />
        <h1 className="font-display mt-3 text-[18px] font-semibold text-token-primary">This link has expired</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-token-secondary">
          Report links stop working after a while so old figures do not keep circulating.
          {when && ` This one expired on ${when}.`}
        </p>
        <p className="mt-2 text-[14px] leading-relaxed text-token-secondary">
          Ask whoever sent it for a fresh link, and they can send the same period again.
        </p>
      </Shell>
    )
  }

  if (problem || !meta?.name) {
    return (
      <Shell>
        <LinkIcon className="h-6 w-6 text-token-muted" aria-hidden />
        <h1 className="font-display mt-3 text-[18px] font-semibold text-token-primary">This link does not work</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-token-secondary">
          It may have been mistyped, cut short by an email client, or withdrawn. Ask whoever sent it for a new one.
        </p>
      </Shell>
    )
  }

  const config = normaliseConfig(meta.config)
  const accent = config.accent || meta.company?.accent || FALLBACK_ACCENT
  const from = meta.range_start!
  const to = meta.range_end!

  return (
    <Shell>
      <ReportHeader
        title={meta.name}
        from={from}
        to={to}
        timezone={meta.company?.timezone}
        branding={{
          accent,
          companyName: meta.company?.name,
          logo: meta.company?.logo,
          showLogo: config.show_logo,
        }}
      />

      <ReportSections sections={config.sections} data={data!.sections} accent={accent} />

      <footer className="mt-8 flex items-center justify-between border-t border-token-subtle pt-3 print:mt-4">
        <p className="text-[11.5px] text-token-muted">
          Figures cover {from} to {to} and do not change after this link was created.
        </p>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 rounded-[10px] border border-token px-2.5 py-1.5 text-[12.5px] font-medium text-token-secondary hover:bg-token-hover print:hidden"
        >
          <Printer className="h-3.5 w-3.5" aria-hidden /> Print
        </button>
      </footer>
    </Shell>
  )
}
